import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logWarn } from "@/lib/log";

const ProgressContext = createContext(null);

export const ProgressProvider = ({ children }) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState({
    completed_stages: [], checklist: {}, trip_type: "umrah", trip_date: null, favorite_qari: null, calc_method: null, favorite_muezzin: null, favorite_duas: [], favorite_packages: [], visited_ziyarah: {}, custom_checklist: [],
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      api.get("/progress")
        .then((res) => {
          const data = res.data || {};
          // Merge any legacy localStorage-only ziyarah visits so nothing is lost on first sync.
          let vz = { ...(data.visited_ziyarah || {}) };
          try {
            const legacy = JSON.parse(localStorage.getItem("manasik-ziyarah-visited") || "[]");
            let changed = false;
            legacy.forEach((id) => { if (!vz[id]) { vz[id] = new Date().toISOString(); changed = true; } });
            if (changed) { data.visited_ziyarah = vz; api.put("/progress", data).catch(() => {}); }
          } catch (e) { console.warn("legacy ziyarah migration skipped", e); }
          data.visited_ziyarah = vz;
          setProgress(data);
        })
        .catch((e) => logWarn("Failed to load progress:", e))
        .finally(() => setLoaded(true));
    }
  }, [user]);

  const persist = useCallback(async (next) => {
    setProgress(next);
    try { await api.put("/progress", next); } catch (e) { logWarn("Failed to persist progress (kept locally):", e); }
  }, []);

  const toggleStage = (stageId) => {
    const set = new Set(progress.completed_stages);
    set.has(stageId) ? set.delete(stageId) : set.add(stageId);
    persist({ ...progress, completed_stages: Array.from(set) });
  };

  const toggleChecklist = (itemId) => {
    const checklist = { ...progress.checklist, [itemId]: !progress.checklist[itemId] };
    persist({ ...progress, checklist });
  };

  const addCustomChecklist = (item) => persist({ ...progress, custom_checklist: [...(progress.custom_checklist || []), item] });
  const removeCustomChecklist = (id) => {
    const checklist = { ...progress.checklist };
    delete checklist[id];
    persist({ ...progress, checklist, custom_checklist: (progress.custom_checklist || []).filter((c) => c.id !== id) });
  };

  const updateTrip = (fields) => persist({ ...progress, ...fields });

  const setFavoriteQari = (voiceId) => persist({ ...progress, favorite_qari: voiceId });

  const setCalcMethod = (method) => persist({ ...progress, calc_method: method });

  const setFavoriteMuezzin = (muezzin) => persist({ ...progress, favorite_muezzin: muezzin });

  const toggleFavoritePackage = (pkgId) => {
    const set = new Set(progress.favorite_packages || []);
    set.has(pkgId) ? set.delete(pkgId) : set.add(pkgId);
    persist({ ...progress, favorite_packages: Array.from(set) });
  };

  const toggleFavoriteDua = (duaId) => {
    const set = new Set(progress.favorite_duas || []);
    set.has(duaId) ? set.delete(duaId) : set.add(duaId);
    persist({ ...progress, favorite_duas: Array.from(set) });
  };

  const toggleZiyarah = (id) => {
    const cur = progress.visited_ziyarah || {};
    const next = { ...cur };
    if (next[id]) delete next[id]; else next[id] = new Date().toISOString();
    persist({ ...progress, visited_ziyarah: next });
  };

  return (
    <ProgressContext.Provider value={{ progress, loaded, toggleStage, toggleChecklist, addCustomChecklist, removeCustomChecklist, updateTrip, setFavoriteQari, setCalcMethod, setFavoriteMuezzin, toggleFavoriteDua, toggleFavoritePackage, toggleZiyarah }}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => useContext(ProgressContext);
